import React, { useState, useEffect, useContext } from "react";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import http from "../services/http";
import Input from "../components/common/input";
import Select from "../components/common/select";
import SidebarContext from "../context/sidebarContext";
import DeleteModal from "../components/common/DeleteModal";
import AnalysisParmsModal from "../components/analysisParms";
import AnalysisTypesModal from "../components/analysisTypes";
import Joi from "joi";

const initialTypeData = {
  code: "",
  name: "",
};

const initialAnalysisData = {
  code: "",
  name: "", 
  type: "",
  low_abs: "",
  low_trigger: "",
  high_trigger: "",
  high_abs: "",
};

const CCFSettings = () => {
  const [modalErrors, setModalErrors] = useState({});
  const [types, setTypes] = useState([]);
  const [analysis, setAnalysis] = useState([]);
  const [showType, setShowType] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [deleteAction, setDeleteAction] = useState();
  const [deleteItem, setDeleteItem] = useState();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteHeader, setDeleteHeader] = useState();
  const [analysisParmsHeader, setAnalysisParmsHeader] = useState();
  const [analysisParmsList, setAnalysisParmsList] = useState();
  const [showAnalysisParmsModal, setShowAnalysisParmsModal] = useState();
  const [analysisTypesHeader, setAnalysisTypesHeader] = useState();
  const [analysisTypesList, setAnalysisTypesList] = useState();
  const [showAnalysisTypesModal, setShowAnalysisTypesModal] = useState();
  const [data, setData] = useState({});
  const [submitAction, setSubmitAction] = useState();

  const [sidebar] = useContext(SidebarContext);

  const typeSchema = Joi.object({
    code: Joi.string().required().min(1).max(3).label("Code"),
    name: Joi.string().required().min(1).max(20).label("Name")
  });

  const analysisSchema = Joi.object({
    code: Joi.string().required().min(1).max(10).label("Code"),
    name: Joi.string().required().min(1).max(100).label("Name"),
    type: Joi.string().required().min(1).max(3).label("CCF Type"),
    low_abs: Joi.number().allow("").allow(null).label("Low Absolute Limit"),
    low_trigger: Joi.number().allow("").allow(null).label("Low Trigger Limit"),
    high_trigger: Joi.number().allow("").allow(null).label("High Trigger Limit"),
    high_abs: Joi.number().allow("").allow(null).label("High Absolute Limit")
  });
  
  useEffect(() => {
    async function fetchAll() {
      try {
        const { data: types } = await http.get(`/ccf-types`);
        setTypes(types);
      }
      catch {
        //ignore error
      }

      try {
        const { data: analysis } = await http.get(`/ccf-analysis`);
        setAnalysis(analysis);
      }
      catch {
        //ignore error
      }
    }

    fetchAll();
  }, []);

  const handleShowType = () => {
    setData(initialTypeData);
    setSubmitAction("insert");
    setModalErrors({});
    setShowType(true);
  };

  const handleShowAnalysis = () => {
    setData(initialAnalysisData);
    setSubmitAction("insert");
    setModalErrors({});
    setShowAnalysis(true);
  };
  
  const handleClickAnalysis = (a) => {
    setData({
      code: a.code,
      name: a.name,
      type: a.ccf_type,
      low_abs: a.low_abs,
      low_trigger: a.low_trigger,
      high_trigger: a.high_trigger,
      high_abs: a.high_abs,
    });
    setSubmitAction("update");
    setShowAnalysis(true);
  };

  const handleChange = ({ currentTarget: input }) => {
    const { name, value } = input;
    setData((d) => {
      return { ...d, [name]: value };
    });
  };

  const handleTypeSubmit = async (e) => {
    const typeErrors = await validateType(data, types);
    setModalErrors(typeErrors || {});
    if (Object.keys(typeErrors).length > 0) return;

    try {
      let message = '';

      if (submitAction === "insert") {
        message = 'CCF Type created.';
        await typeInsert();
      }
      if (submitAction === "update") {
        message = 'CCF Type updated.';
        await typeUpdate();
      }
      setShowType(false);

      toast.success(message);
    } catch (error) {
      if (error.response && error.response.status < 500) {
        toast.error(error.response.data);
      }
    }
  };

  const validateType = async (type, list) => {
    const errors = {};

    const options = { abortEarly: false };
    const typeError = typeSchema.validate(type, options);
    
    if (typeError.error) {
      for (let item of typeError.error.details) errors[item.path[0]] = item.message;
    }

    if (type.code !== "") {
        list.forEach (a => {
        if (type.code.trim().toLowerCase() === a.code.trim().toLowerCase()) {
          errors["code"] = "Must be unique";
        }
      });
    }

    if (type.name !== "") {
      list.forEach (a => {
        if (type.name === a.name) {
          errors["name"] = "Must be unique";
        }
      });
    }

    return errors.length === 0 ? null : errors;
  }

  const typeInsert = async () => {
    try {
      const { data: newType } = await http.post(`ccf-types`, data);
      setTypes([...types, newType]);

    }
    catch {
      //ignore error
    }
  };

  const typeUpdate = async () => {
    try {
      const { data: newType } = await http.post(`ccf-types`, data);
      setTypes([...types, newType]);
    }
    catch {
      //ignore error
    }
  };

  const handleAnalysisSubmit = async (e) => {
    const analysisErrors = await validateAnalysis(data, analysis);
    setModalErrors(analysisErrors || {});
    if (Object.keys(analysisErrors).length > 0) return;

    data.low_abs = cleanValue(data.low_abs);
    data.low_trigger = cleanValue(data.low_trigger);
    data.high_trigger = cleanValue(data.high_trigger);
    data.high_abs = cleanValue(data.high_abs);

    try {
      let message = '';
      if (submitAction === "insert") {
        message = 'CCF Analysis created.'; 
        await analysisInsert();
      }
      if (submitAction === "update") {
        message = 'CCF Analysis updated.';
        await analysisUpdate();
      }
      setShowAnalysis(false);

      toast.success(message);
    } catch (error) {
      if (error.response && error.response.status < 500) {
        toast.error(error.response.data);
      }
    }
  };

  function cleanValue(newValue) {
    if (newValue !== undefined && newValue !== '') return newValue;
    
    return null;
  }

  async function validateAnalysis(analysis, list) {
    const la = analysis.low_abs != null && analysis.low_abs !== "";
    const lt = analysis.low_trigger != null && analysis.low_trigger !== "";
    const ht = analysis.high_trigger != null && analysis.high_trigger !== "";
    const ha = analysis.high_abs != null && analysis.high_abs !== "";
  
    const errors = {};
    const invalidLimitValue = "Must be greater than or equal to ";
    // const missingAbsoluteLimit = "High Absolute Limit or Low Absolute Limit, or both, must be entered";
    
    const options = { abortEarly: false };
    const analysisError = analysisSchema.validate(analysis, options);
    if (analysisError.error) {
      for (let item of analysisError.error.details) errors[item.path[0]] = item.message;
    }

    if (submitAction === "insert") {
      if (analysis.code !== "") {
        list.forEach (a => {
          if (analysis.code.trim().toLowerCase() === a.code.trim().toLowerCase()) {
            errors["code"] = "Must be unique";
          }
        });
      }

      if (analysis.name !== "") {
        list.forEach (a => {
          if (analysis.name === a.name) {
            errors["name"] = "Must be unique";
          }
        });
      }
    }

    if (analysis.type === "") {
      errors["type"] = "CCF Type must be selected";
    }

    const lowAbs = Number(analysis.low_abs);
    const lowTrigger = Number(analysis.low_trigger);
    const highTrigger = Number(analysis.high_trigger);
    const highAbs = Number(analysis.high_abs);

    // if (!la && !ha) { //Must have at least one of these
    //   errors["low_abs"] = missingAbsoluteLimit;
    //   errors["high_abs"] = missingAbsoluteLimit;
    // }
  
    // if (lt && !la) { //Cannot have Low Check Limit without Low Absolute Limit
    //   errors["low_abs"] = "When Low Trigger Limit is entered Low Absolute Limit must also be entered";
    // }
  
    // if (ht && !ha) { //Cannot have High Trigger Limit without High Absolute Limit
    //   errors["high_abs"] = "When High Trigger Limit is entered High Absolute Limit must also be entered";
    // }

    // if (ht && ha) {
    //   errors["high_abs"] = "Cannot have both High Trigger and High Absolute";
    // }
  
    if (ht && ha && highAbs < highTrigger) { //High Absolute Limit must be greater or equal to High Trigger Limit
      errors["high_abs"] = invalidLimitValue + "High Trigger Limit";
    }
  
    if (ha && lt && highAbs < lowTrigger) { //High Absolute Limit must be greater or equal to Low Trigger Limit
      errors["high_abs"] = invalidLimitValue + "Low Trigger Limit";
    }
  
    if (la && ha && highAbs < lowAbs) { //High Absolute Limit must be greater or equal to Low Absolute Limit
      errors["high_abs"] = invalidLimitValue + "Low Absolute Limit";
    }
  
    if (lt && ht && highTrigger < lowTrigger) { //High Trigger Limit must be greater or equal to Low  Trigger Limit
      errors["high_trigger"] = invalidLimitValue + "Low Trigger Limit";
    }
  
    if (ht && la && highTrigger < lowAbs) { //High Trigger Limit must be greater or equal to Low Absolute Limit
      errors["high_trigger"] = invalidLimitValue + "Low Absolute Limit";
    }
  
    if (la && lt && lowTrigger < lowAbs) { //Low Trigger Limit must be greater or equal to Low Absolute Limit
      errors["low_trigger"] = invalidLimitValue + "Low Absolute Limit";
    }
  
    return errors.length === 0 ? null : errors;
  }
  
  const analysisInsert = async () => {
    try {
      const { data: newAnalysis } = await http.post(`ccf-analysis`, data);
      setAnalysis([...analysis, newAnalysis]);
    }
    catch {
      //ignore error
    }
  };

  const analysisUpdate = async () => {
    try {
      const { data: newData } = await http.put(`ccf-analysis`, data);
      const newAnalysis = [...analysis];
      const index = newAnalysis.findIndex((a) => a.code === data.code);
      newAnalysis[index] = newData;
      setAnalysis(newAnalysis);
    }
    catch {
      //ignore error
    }
  };

  const deleteClose = async (response) => {
    if (response === "Yes") {
      switch (deleteAction) {
        case "type":
          await applyDeleteType(deleteItem);
          break;
        case "analysis":
          await applyDeleteAnalysis(deleteItem);
          break;
        default:
          //do nothing
          break;
      }
    }

    setShowDeleteModal(false);
  };

  const handleDeleteType = async (type) => {
    try {
      const { data: types } = await http.get(`ccf-analysis/used/${type.code}`, data);

      if (types) {
        setAnalysisTypesHeader(`${type.code} is assigned to one or more analyses`);
        setAnalysisTypesList(types);
        setShowAnalysisTypesModal(true);
      }
      else {
        setDeleteAction("type");
        setDeleteItem(type);
        setDeleteHeader("Delete CCF Type");
        setShowDeleteModal(true);
      }
    }
    catch {
      //ignore error
    }
  }

  const analysisTypesClose = async (response) => {
    setShowAnalysisTypesModal(false);
  };

  const applyDeleteType = async (type) => {
    const oldTypes = [...types];

    try {
      await http.delete(`ccf-types/${type.code}`);

      setTypes((ty) => ty.filter((t) => t.code !== type.code));      toast.success(`${type.name} removed`);
    } catch (error) {
      setTypes(oldTypes);
      if (error.response && error.response.status < 500) {
        toast.error(error.response.data);
      }
    }
  };

  const handleDeleteAnalysis = async (analyte) => {
    try {
      const { data: parameters } = await http.get(`ccf-analysis/delete/${analyte.code}`)

      if (parameters) {
        setAnalysisParmsHeader(`${analyte.code} is assigned to one or more parameters`);
        setAnalysisParmsList(parameters);
        setShowAnalysisParmsModal(true);
      }
      else {
        setDeleteAction("analysis");
        setDeleteItem(analyte);
        setDeleteHeader("Delete CCF Analysis");
        setShowDeleteModal(true);
      }
    }
    catch {
      //ignore error
    }
  }

  const analysisParmsClose = async (response) => {
    setShowAnalysisParmsModal(false);
  };

  const applyDeleteAnalysis = async (analyte) => {
    const oldAnalysis = [...analysis];
    setAnalysis((an) => an.filter((a) => a.code !== analyte.code));
    try {
      await http.delete(`ccf-analysis/${analyte.code}`);
      toast.success(`${analyte.name} removed`);
    } catch (error) {
      setAnalysis(oldAnalysis);
      if (error.response && error.response.status < 500) {
        toast.error(error.response.data);
      }
    }
  };

  const deleteName = deleteItem ? deleteItem.name : "";

  return (
    <div className={sidebar ? "col-10 mt-4" : "col-12 mt-4"}>
        <div className="row">
          <div className="col-6">
            <h2>CCF Types</h2>
          </div>
          <div className="col-6">
            <button className="btn btn-primary btn-sm" onClick={handleShowType}>
              <i className="fas fa-plus"></i>
            </button>
          </div>
        </div>
        <div className="row">
          <div className="col">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th style={{width: 100}}>Code</th>
                  <th>Name</th>
                  <th style={{width: 64}}/>
                </tr>
              </thead>
              <tbody>
                {types.map((type) => (
                  <tr key={type.code}>
                    <td style={{width: 100}}>{type.code}</td>
                    <td>{type.name}</td>
                    <td style={{width: 64}}>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDeleteType(type)}>
                        <i className="fas fa-trash" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <hr/>
        <div className="row">
          <div className="col-6">
            <h2>CCF Analysis</h2>
          </div>
          <div className="col-6">
            <button className="btn btn-primary btn-sm" onClick={handleShowAnalysis}>
              <i className="fas fa-plus"></i>
            </button>
          </div>
        </div>
        <div className="row">
          <div className="col">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th style={{width: 100}}>Code</th>
                  <th>Name</th>
                  <th>CCF Type</th>
                  <th>Low Abs</th>
                  <th>Low Trigger</th>
                  <th>High Trigger</th>
                  <th>High Abs</th>
                  <th style={{width: 64}} />
                </tr>
              </thead>
              <tbody>
                {analysis.map((an) => (
                  <tr key={an.code}>
                    <td style={{width: 100}}>
                      <button className="btn btn-sm btn-link" onClick={() => handleClickAnalysis(an)}>
                        {an.code}
                      </button>
                    </td>
                    <td>{an.name}</td>
                    <td>{an.type.name}</td>
                    <td>{an.low_abs}</td>
                    <td>{an.low_trigger}</td>
                    <td>{an.high_trigger}</td>
                    <td>{an.high_abs}</td>
                    <td style={{width: 64}}>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDeleteAnalysis(an)}>
                        <i className="fas fa-trash" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <hr/>
      <TypeModal
        show={showType}
        data={data}
        handleChange={handleChange}
        handleSubmit={handleTypeSubmit}
        handleClose={() => setShowType(false)}
        errors={modalErrors}
      />
      <AnalysisModal
        show={showAnalysis}
        data={data}
        action = {submitAction}
        handleChange={handleChange}
        handleSubmit={handleAnalysisSubmit}
        handleClose={() => setShowAnalysis(false)}
        types={types}
        errors={modalErrors}
      />
      <DeleteModal
        show={showDeleteModal}
        handleClose={deleteClose}
        header={deleteHeader}
        description={deleteName}
      />
      <AnalysisParmsModal
        show={showAnalysisParmsModal}
        handleClose={analysisParmsClose}
        header={analysisParmsHeader}
        parameters={analysisParmsList}
      />
      <AnalysisTypesModal
        show={showAnalysisTypesModal}
        handleClose={analysisTypesClose}
        header={analysisTypesHeader}
        types={analysisTypesList}
      />
    </div>
  );
};

const TypeModal = ({
  data,
  show = false,
  handleChange,
  handleSubmit,
  handleClose,
  errors
}) => {
  return (
    <Modal
      show={show}
      onHide={handleClose}
      errors={errors}>
      <Modal.Header closeButton className="bg-warning">
        <h3>Type</h3>
      </Modal.Header>
      <Modal.Body>
        <Input
          name="code"
          label="Code"
          placeholder="code"
          value={data.code}
          onChange={handleChange}
          error={errors.code}
        />
        <Input
          name="name"
          label="Name"
          placeholder="name"
          value={data.name}
          onChange={handleChange}
          error={errors.name}
        />
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-primary" onClick={handleSubmit}>
          Create
        </button>
      </Modal.Footer>
    </Modal>
  );
};

const AnalysisModal = ({
  data,
  show = false,
  action,
  handleChange,
  handleSubmit,
  handleClose,
  types,
  errors
}) => {

  const caption = action === "insert" ? "Create" : "Update";

  return (
    <Modal
      show={show}
      onHide={handleClose}
      errors={errors}>
      <Modal.Header closeButton className="bg-warning">
        <h3>Analysis</h3>
      </Modal.Header>
      <Modal.Body>
        <div className="form-row">
          <div className="col-3">
            <Input
              name="code"
              label="Code"
              placeholder="code"
              value={data.code}
              onChange={handleChange}
              error={errors.code}
            />
          </div>
          <div className="col-9">
            <Input
              name="name"
              label="Name"
              placeholder="name"
              value={data.name}
              onChange={handleChange}
              error={errors.name}
            />
          </div>
        </div>
        <Select
          name="type"
          label="CCF Type"
          placeholder="select type..."
          options={types}
          value={data.type}
          onChange={handleChange}
          listKey={"code"}
          listName="name"
          error={errors.type}
        />
        <div className="form-row">
          <div className="col">
            <Input
              name="low_abs"
              label="Low Absolute Limit"
              placeholder="value"
              value={data.low_abs}
              onChange={handleChange}
              error={errors.low_abs}
            />
          </div>
          <div className="col">
            <Input
              name="low_trigger"
              label="Low Trigger Limit"
              placeholder="value"
              value={data.low_trigger}
              onChange={handleChange}
              error={errors.low_trigger}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="col">
            <Input
              name="high_trigger"
              label="High Trigger Limit"
              placeholder="value"
              value={data.high_trigger}
              onChange={handleChange}
              error={errors.high_trigger}
            />
          </div>
          <div className="col">
            <Input
              name="high_abs"
              label="High Absolute Limit"
              placeholder="value"
              value={data.high_abs}
              onChange={handleChange}
              error={errors.high_abs}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-primary" onClick={handleSubmit}>
          {caption}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default CCFSettings;
